import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AppDispatch } from "../store";
import axiosInstance from "../../utils/axios";
import axios from "axios";

interface Invoice {
  id: number;
  invoiceNumber: string;
  date: string;
  dueDate: string;
  total: number;
  paid: boolean;
  associationId: number;
  parent?: number | null;
}

interface InvoiceState {
  invoices: Invoice[];
  invoice?: Invoice;
  open: boolean;
  status: "idle" | "loading" | "failed";
}

const initialState: InvoiceState = {
  invoices: [],
  invoice: undefined,
  open: false,
  status: "idle",
};

const slice = createSlice({
  name: "invoice",
  initialState,
  reducers: {
    setInvoices(state, action) {
      state.invoices = action.payload;
    },
    setInvoice(state, action) {
      state.invoice = action.payload;
    },
    openInvoiceForm(state, action) {
      state.open = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchInvoices.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchInvoices.fulfilled, (state, action) => {
        state.status = "idle";
        state.invoices = action.payload;
      })
      .addCase(fetchInvoices.rejected, (state) => {
        state.status = "failed";
      });
  },
});

export const { setInvoices, setInvoice, openInvoiceForm } = slice.actions;
export default slice.reducer;

export const fetchInvoices = createAsyncThunk(
  "invoice/fetchInvoices",
  async (associationId: number) => {
    const response = await axiosInstance.get(
      `/invoices/association/${associationId}`
    );
    return response.data;
  }
);

export function createInvoice(values: Partial<Invoice>, associationId: number) {
  return async (dispatch: AppDispatch) => {
    const response = await axiosInstance.post(`/invoices`, {
      ...values,
      associationId,
    });
    dispatch(fetchInvoices(associationId));
    return response.data;
  };
}

export function generateInvoicesFromUnitList(associationId: number) {
  return async (dispatch: AppDispatch) => {
    try {
      const response = await axiosInstance.post(`/invoices/from-unit-list`, {
        associationId,
      });

      dispatch(fetchInvoices(associationId));
      return response.data;
    } catch (error) {
      console.error("Laskujen generointi epäonnistui", error);
      throw error;
    }
  };
}

export function updateInvoice(id: number, values: Partial<Invoice>) {
  return async (dispatch: AppDispatch) => {
    try {
      const response = await axiosInstance.put(`/invoices/${id}`, values);
      dispatch(fetchInvoices(values.associationId!));
      return response.data;
    } catch (error) {
      throw error;
    }
  };
}

type ApiError = { message?: string; code?: string; details?: unknown };

export const updateInvoicesBatchSent = createAsyncThunk<
  { ids: number[] }, // return type
  { ids: number[]; associationId: number }, // arg type
  { rejectValue: ApiError } // error payload type
>(
  "invoice/updateInvoicesBatchSent",
  async ({ ids, associationId }, { dispatch, rejectWithValue }) => {
    try {
      await axiosInstance.put(`/invoices/batch/sent`, { ids, associationId });

      // If fetchInvoices is also a thunk, unwrap to catch its errors too
      await dispatch(fetchInvoices(associationId)).unwrap();

      return { ids };
    } catch (err: any) {
      if (axios.isAxiosError(err)) {
        return rejectWithValue(
          (err.response?.data as ApiError) ?? { message: err.message }
        );
      }
      // Non-axios error: let RTK handle it as a thrown error
      throw err;
    }
  }
);

export const updateInvoicesBatchReminder = createAsyncThunk<
  { ids: number[] }, // return type
  { ids: number[]; associationId: number }, // arg type
  { rejectValue: ApiError } // error payload type
>(
  "invoice/updateInvoicesBatchReminder",
  async ({ ids, associationId }, { dispatch, rejectWithValue }) => {
    try {
      await axiosInstance.put(`/invoices/batch/reminder`, {
        ids,
        associationId,
      });

      await dispatch(fetchInvoices(associationId)).unwrap();
      return { ids };
    } catch (err: any) {
      if (axios.isAxiosError(err)) {
        return rejectWithValue(
          (err.response?.data as ApiError) ?? { message: err.message }
        );
      }
      // Non-axios error: let RTK handle it as a thrown error
      throw err;
    }
  }
);

export const updateInvoicesBatchPaid = createAsyncThunk<
  { ids: number[] }, // return type
  { ids: number[]; associationId: number }, // arg type
  { rejectValue: ApiError } // error payload type
>(
  "invoice/updateInvoicesBatchPaid",
  async ({ ids, associationId }, { dispatch, rejectWithValue }) => {
    try {
      await axiosInstance.put(`/invoices/batch/paid`, { ids, associationId });
      await dispatch(fetchInvoices(associationId)).unwrap();
      return { ids };
    } catch (err: any) {
      if (axios.isAxiosError(err)) {
        return rejectWithValue(
          (err.response?.data as ApiError) ?? { message: err.message }
        );
      }
      // Non-axios error: let RTK handle it as a thrown error
      throw err;
    }
  }
);

export function deleteInvoicesBatch(ids: number[], associationId: number) {
  return async (dispatch: AppDispatch) => {
    try {
      await axiosInstance.delete(`/invoices/batch/delete`, {
        data: ids,
      });
      dispatch(fetchInvoices(associationId!));
    } catch (error) {
      throw error;
    }
  };
}

export function deleteInvoice(id: number, associationId: number) {
  return async (dispatch: AppDispatch) => {
    await axiosInstance.delete(`/invoices/${id}`);
    dispatch(fetchInvoices(associationId));
  };
}
